/**
 * Géocodage des pays via l'API REST Countries (gratuite, sans clé).
 * Fournit coordonnées (centre pays) et nom pour un code ISO 3166-1 alpha-2.
 */

const API_BASE = 'https://restcountries.com/v3.1';

export interface CountryGeo {
    code: string;
    name: string;
    latlng: [number, number];
}

const cache = new Map<string, CountryGeo>();

/** Fallback statique quand l’API est bloquée (CSP) ou indisponible. */
const FALLBACK: Record<string, [number, number]> = {
    FR: [46.2276, 2.2137], BE: [50.5039, 4.4699], LU: [49.8153, 6.1296], CH: [46.8182, 8.2275],
    DE: [51.1657, 10.4515], IT: [41.8719, 12.5674], ES: [40.4637, -3.7492], PT: [39.3999, -8.2245],
    GB: [55.3781, -3.4360], IE: [53.4129, -8.2439], NL: [52.1326, 5.2913], AT: [47.5162, 14.5501],
    RE: [-21.1151, 55.5364], GP: [16.265, -61.551], MQ: [14.6415, -61.0242], GF: [3.9339, -53.1258], YT: [-12.8275, 45.1662],
};

/** Normalise le code pays (ex. UK → GB pour l’API REST Countries). */
export function normalizeCountryCode(code: string): string {
    const upper = code.toUpperCase().trim();
    if (upper === 'UK') return 'GB';
    return upper;
}

function normalizeCode(code: string): string {
    return normalizeCountryCode(code);
}

/**
 * Récupère les infos géo (nom + coordonnées) pour un ou plusieurs codes pays.
 * Résultats mis en cache en mémoire.
 */
export async function getCountryGeo(codes: string[]): Promise<Map<string, CountryGeo>> {
    const unique = [...new Set(codes.map(normalizeCode).filter(Boolean))];
    const missing = unique.filter((code) => !cache.has(code));
    const result = new Map<string, CountryGeo>();

    for (const code of unique) {
        const cached = cache.get(code);
        if (cached) result.set(code, cached);
    }

    if (missing.length === 0) return result;

    try {
        const res = await fetch(
            `${API_BASE}/alpha?codes=${missing.join(',')}&fields=cca2,name,latlng`
        );
        if (!res.ok) return result;

        const list = (await res.json()) as Array<{
            cca2?: string;
            name?: { common?: string; official?: string };
            latlng?: [number, number];
        }>;

        for (const item of list) {
            const code = (item.cca2 || '').toUpperCase();
            const latlng = item.latlng;
            const name = item.name?.common || item.name?.official || code;
            if (!code || !latlng || latlng.length < 2) continue;

            const geo: CountryGeo = { code, name, latlng: [latlng[0], latlng[1]] };
            cache.set(code, geo);
            result.set(code, geo);
        }
    } catch (err) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[country-geocode] fetch failed:', err);
        }
    }

    // Fallback : utiliser les coordonnées statiques si l’API a échoué ou été bloquée (CSP)
    for (const code of missing) {
        if (result.has(code)) continue;
        const latlng = FALLBACK[code];
        if (latlng) {
            const geo: CountryGeo = { code, name: code, latlng };
            cache.set(code, geo);
            result.set(code, geo);
        }
    }

    return result;
}

/**
 * Récupère les infos géo pour les codes présents dans salesByCountry.
 * Utilisé par la carte Analytics (BusinessSection).
 */
export async function getCountryGeoForSales(
    salesByCountry: { country: string }[]
): Promise<Map<string, CountryGeo>> {
    const codes = salesByCountry.map((c) => c.country).filter(Boolean);
    return getCountryGeo(codes);
}
